import React, { useState } from 'react'
import DashModalV2 from '../../../components/DashModalV2'
import { Btn, COLORS, EmptyState, Field, INPUT_STYLE, Kpi, fmtDate, fmtShort, lb, money, todayIso, useNarrow, useSaving } from '../FieldOpsKit'
import { WINDOWS } from '../demoData'

/**
 * Ready to deliver: invoices that finished picking and have no delivery yet. Sage does not say which invoices are
 * delivered by our own trucks (ShipVia is empty), so the dispatcher decides here and adds them to the board.
 */
export default function ReadyTab({ ctx, goBoard }) {
    const drv = ctx.state.drivers
    const [sel, setSel] = useState({})
    const [modal, setModal] = useState(false)
    const narrow = useNarrow(800)
    const chosen = drv.ready.filter((r) => sel[r.invoiceNo])
    const allOn = drv.ready.length > 0 && chosen.length === drv.ready.length

    if (!ctx.can.dispatch) return <EmptyState title="Dispatchers only" text="Only administrators can add deliveries." />

    return (
        <React.Fragment>
            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3"><Kpi label="Ready to deliver" value={drv.ready.length} sub="picked, no delivery yet" status={drv.ready.length > 0 ? 'warning' : 'success'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Total weight" value={lb(drv.ready.reduce((a, r) => a + r.weightLb, 0))} sub="all ready invoices" /></div>
                <div className="col-6 col-lg-3"><Kpi label="Selected" value={chosen.length} sub={lb(chosen.reduce((a, r) => a + r.weightLb, 0))} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Invoice amount" value={money(chosen.reduce((a, r) => a + r.amount, 0))} sub="of the selection" /></div>
            </div>

            {drv.ready.length === 0 ? <EmptyState title="Everything is on the board" text="No picked invoices are waiting for a delivery." action={<Btn kind="secondary" onClick={goBoard}>Go to the delivery board</Btn>} /> : (
                <React.Fragment>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Btn kind="secondary" small onClick={() => { const n = {}; if (!allOn) drv.ready.forEach((r) => { n[r.invoiceNo] = true }); setSel(n) }}>{allOn ? 'Clear selection' : 'Select all'}</Btn>
                        <Btn disabled={chosen.length === 0} onClick={() => setModal(true)}>Add {chosen.length || ''} to deliveries</Btn>
                    </div>
                    {narrow ? drv.ready.map((r) => (
                        <label key={r.invoiceNo} style={{ display: 'flex', gap: 10, background: '#FFFFFF', border: '1px solid ' + (sel[r.invoiceNo] ? COLORS.blue : COLORS.line), borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                            <input type="checkbox" checked={!!sel[r.invoiceNo]} onChange={(e) => setSel({ ...sel, [r.invoiceNo]: e.target.checked })} />
                            <span><strong style={{ color: COLORS.navy }}>{r.customer}</strong><br /><span style={{ fontSize: '0.75rem', color: COLORS.slate }}>{r.invoiceNo} · {r.city} · {lb(r.weightLb)} · {money(r.amount)}</span></span>
                        </label>
                    )) : (
                        <div className="tableFixHeadSmall"><table className="table table-clean">
                            <thead><tr><th style={{ width: 28 }}></th><th>Invoice</th><th>Customer</th><th>Address</th><th>Invoiced</th><th>Weight</th><th>Amount</th></tr></thead>
                            <tbody>{drv.ready.map((r) => (
                                <tr key={r.invoiceNo} style={{ cursor: 'pointer' }} onClick={() => setSel({ ...sel, [r.invoiceNo]: !sel[r.invoiceNo] })}>
                                    <td><input type="checkbox" checked={!!sel[r.invoiceNo]} onChange={() => undefined} aria-label={'Select ' + r.invoiceNo} /></td>
                                    <td style={{ fontFamily: 'monospace' }}>{r.invoiceNo}</td>
                                    <td style={{ fontWeight: 600 }}>{r.customer}</td>
                                    <td style={{ color: COLORS.slate }}>{r.address}, {r.city}</td>
                                    <td>{fmtShort(r.invoiceDate)}</td><td>{lb(r.weightLb)}</td><td>{money(r.amount)}</td>
                                </tr>
                            ))}</tbody>
                        </table></div>
                    )}
                </React.Fragment>
            )}
            {modal ? <AddModal ctx={ctx} items={chosen} onClose={() => setModal(false)} onDone={() => { setSel({}); setModal(false) }} goBoard={goBoard} /> : null}
        </React.Fragment>
    )
}

function AddModal({ ctx, items, onClose, onDone, goBoard }) {
    const drv = ctx.state.drivers
    const [date, setDate] = useState(todayIso())
    const [driverId, setDriverId] = useState('')
    const [window_, setWindow] = useState(WINDOWS[0])
    const [notes, setNotes] = useState('')
    const [busy, run] = useSaving()
    const bad = !date || date < todayIso()
    const submit = () => {
        if (bad) return
        run(() => {
            ctx.dispatch({ type: 'DELIVERY_ADD', items: items, date: date, driverId: driverId || null, window: window_, notes: notes, by: ctx.me.name })
            ctx.toast(items.length + ' deliver' + (items.length === 1 ? 'y' : 'ies') + ' added for ' + fmtDate(date) + '.')
            onDone()
            if (date) setTimeout(() => goBoard(), 0)
        })
    }
    return (
        <DashModalV2 show onClose={onClose} title="Add to deliveries" subtitle={items.length + ' invoice' + (items.length === 1 ? '' : 's') + ' · ' + lb(items.reduce((a, r) => a + r.weightLb, 0))} size="md"
            footer={<React.Fragment><Btn kind="secondary" onClick={onClose}>Cancel</Btn><Btn busy={busy} disabled={bad} onClick={submit}>Add to board</Btn></React.Fragment>}>
            <Field label="Delivery date" error={bad ? 'Pick today or a later date' : ''}><input type="date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} style={INPUT_STYLE} /></Field>
            <Field label="Driver (optional)" hint="You can assign it later from the board."><select value={driverId} onChange={(e) => setDriverId(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }}>
                <option value="">Unassigned</option>
                {drv.drivers.filter((d) => d.active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></Field>
            <Field label="Time window"><select value={window_} onChange={(e) => setWindow(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }}>{WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}</select></Field>
            <Field label="Delivery notes (optional)"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...INPUT_STYLE, width: '100%', minHeight: 60 }} placeholder="Gate code, call before arriving…" /></Field>
        </DashModalV2>
    )
}
